import { useState } from 'react'
import { faFile } from '@fortawesome/pro-solid-svg-icons'
import { Icon, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import type { AttachedFile } from '../lib/types'

interface AttachedFilesProps {
  files: AttachedFile[]
}

const typeLabel = ({ fileName, contentType }: AttachedFile): string => {
  const extension = fileName.split('.').pop()
  if (extension && extension !== fileName) return extension.toUpperCase()
  const subtype = contentType.split('/')[1]
  return subtype ? subtype.toUpperCase() : 'FILE'
}

const AttachedFileCard = ({ file }: { file: AttachedFile }) => {
  // A format the browser accepts but cannot decode, such as HEIC, reaches
  // onError rather than rendering, so the icon has to be a runtime fallback.
  const [previewFailed, setPreviewFailed] = useState(false)
  const showPreview = Boolean(file.previewUrl) && !previewFailed

  return (
    <div
      className={mtc(
        'w-[150px] max-w-full overflow-hidden',
        'rounded-[8px] border border-moss-25 bg-neutral-0'
      )}
    >
      {showPreview ? (
        <img
          src={file.previewUrl}
          alt={file.fileName}
          onError={() => setPreviewFailed(true)}
          className="block h-[84px] w-full object-cover"
        />
      ) : (
        <div className="flex h-[84px] w-full items-center justify-center bg-moss-25">
          <Icon icon={faFile} className="text-[20px] text-neutral-500" />
        </div>
      )}
      <div className="flex min-w-0 flex-col gap-0.5 px-2 py-1.5">
        <Text
          variant={TypographyStyles.LABEL_SM}
          color={TextColors.HEADER}
          className="truncate"
          title={file.fileName}
        >
          {file.fileName}
        </Text>
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          {typeLabel(file)}
        </Text>
      </div>
    </div>
  )
}

export const AttachedFiles = ({ files }: AttachedFilesProps) => {
  if (files.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2 pt-2">
      {files.map((file, index) => (
        <AttachedFileCard key={`${file.fileName}-${index}`} file={file} />
      ))}
    </div>
  )
}
